// Retry scheduling for the support-chat outbox.
//
// Pure and injectable: `now` and `random` are parameters so the ladder can be asserted
// exactly in tests without faking timers or Math.random globally.

/**
 * Delay ladder in ms, indexed by attempts already made. Past the end it holds at the
 * last value rather than growing without bound — an agent who walks back into signal
 * after 20 minutes should not wait another hour.
 */
export const RETRY_LADDER_MS = [2_000, 5_000, 15_000, 45_000, 120_000, 300_000] as const;

/**
 * Attempts after which an item stops retrying on its own and waits for a manual Retry.
 * It is HELD, never deleted: discarding an agent's unsent message about a blocked
 * polling unit is not an acceptable failure mode.
 */
export const MAX_ATTEMPTS = 6;

/**
 * After this long in `held`, the UI offers the phone number. If the app cannot get a
 * message out, it should say so and name the fallback rather than let the agent
 * conclude nobody is listening.
 */
export const HELD_ESCALATION_MS = 5 * 60_000;

/**
 * Full jitter: a uniform pick in [0, base]. Not base + jitter.
 *
 * Two hundred agents at one collation centre lose signal and regain it together, so
 * their retries are already synchronised. A fixed ladder — or a narrow jitter band —
 * reconverges them into a thundering herd on the first cell tower back. Full jitter
 * spreads the same population across the whole window.
 */
export const backoffDelayMs = (attempts: number, random: () => number = Math.random): number => {
	const index = Math.min(Math.max(attempts, 0), RETRY_LADDER_MS.length - 1);
	const base = RETRY_LADDER_MS[index];
	return Math.floor(random() * base);
};

/** Absolute epoch-ms time an item with this many attempts may next be tried. */
export const nextAttemptAt = (attempts: number, now: number, random: () => number = Math.random): number =>
	now + backoffDelayMs(attempts, random);

export const hasExhaustedAttempts = (attempts: number): boolean => attempts >= MAX_ATTEMPTS;

/** Whether an item is eligible for this flush pass. */
export const isDue = (item: { state: string; nextAttemptAt: number }, now: number): boolean =>
	(item.state === 'queued' || item.state === 'failed') && item.nextAttemptAt <= now;

/**
 * Whole seconds until an item may retry, for the in-bubble countdown.
 *
 * The countdown is not decoration. "Trying again in 45s" tells an agent the app is
 * working and to stop tapping; a bare spinner tells them nothing and they tap Send four
 * more times, which is how one stuck message becomes five.
 */
export const secondsUntilRetry = (nextAttempt: number, now: number): number => Math.max(0, Math.ceil((nextAttempt - now) / 1000));
